/**
 * Environment Configuration
 * 
 * This file manages API URLs and AWS resources for different environments.
 * Supports 5 environments: local (development), dev, qa, production, ind
 * 
 * Configuration Hierarchy:
 * 1. Runtime injection via window.APP_CONFIG (from SSM via start.sh)
 * 2. Static fallback configuration (for local development)
 * 
 * Compliance: HIPAA §164.308(a)(4) - Information Access Management
 * Security: Environment-specific resource isolation
 */

/**
 * Static configuration for all environments
 * These are fallback values - deployed environments use window.APP_CONFIG
 */
const ENV_CONFIG = {
  // Local development environment
  development: {
    API_URL: 'http://localhost:8000/api/v1',
    S3_BUCKET: 'aitcare-dashboard-photos-dev', // Dev and local share same bucket
    S3_REGION: 'us-east-1',
  },
  
  // Dev environment (deployed) - shares bucket with local
  dev: {
    API_URL: `${window.location.origin}/api/v1`,
    S3_BUCKET: 'aitcare-dashboard-photos-dev', // Fallback - overridden by SSM
    S3_REGION: 'us-east-1',
  },
  
  // QA environment (deployed) - separate bucket
  qa: {
    API_URL: `${window.location.origin}/api/v1`,
    S3_BUCKET: 'aitcare-dashboard-photos-qa', // Fallback - overridden by SSM
    S3_REGION: 'us-east-1',
  },
  
    // Production environment (deployed) - separate bucket
  production: {
    API_URL: `${window.location.origin}/api/v1`,
    S3_BUCKET: 'aitcare-dashboard-photos-prod', // Fallback - overridden by SSM
    S3_REGION: 'us-east-1',
  },
  
  // India environment (deployed) - separate bucket in ap-south-1
  ind: {
    API_URL: `${window.location.origin}/api/v1`,
    S3_BUCKET: 'aitcare-dashboard-photos-ind', // Fallback - overridden by SSM
    S3_REGION: 'ap-south-1',
  },

  // Sandbox environment (deployed) - shares QA bucket
  sandbox: {
    API_URL: `${window.location.origin}/api/v1`,
    S3_BUCKET: 'fallvision-sandbox-resident-photos-752192503196', // Fallback - overridden by SSM
    S3_REGION: 'us-east-1',
  }
};

/**
 * Determine current environment
 * Priority:
 * 1. REACT_APP_ENV environment variable (explicit override)
 * 2. NODE_ENV (development vs production build)
 * 3. Hostname detection (for deployed environments)
 * 4. Default to 'development'
 */
const getEnvironment = () => {
  // Check if explicitly set via environment variable
  if (process.env.REACT_APP_ENV) {
    const env = process.env.REACT_APP_ENV.toLowerCase();
    console.log(`🔧 Environment set via REACT_APP_ENV: ${env}`);
    return env;
  }
  
  // If NODE_ENV is development, we're in local dev mode
  if (process.env.NODE_ENV === 'development') {
    return 'development';
  }
  
    // For production builds, detect environment from hostname
  const hostname = window.location.hostname;
  
  if (hostname.includes('prod-dashboard') || hostname.includes('production')) {
    return 'production';
  } else if (hostname.includes('qat-dashboard') || hostname.includes('qa-dashboard')) {
    return 'qa';
  } else if (hostname.includes('dev-dashboard')) {
    return 'dev';
  } else if (hostname.includes('portal')) {
    return 'ind';
  } else if (hostname.includes('sandbox-dashboard')) {
    return 'sandbox';
  } else if (hostname.includes('localhost') || hostname.includes('127.0.0.1')) {
    return 'development';
  }
  
    // SECURITY FIX: Fail-secure means STOP, not continue
  // If we can't detect environment, refuse to run
    console.error('❌ CRITICAL: Cannot detect environment from hostname:', hostname);
  console.error('❌ Valid hostnames:');
  console.error('❌   - localhost/127.0.0.1 (development)');
  console.error('❌   - dev-dashboard.ait.care (dev)');
  console.error('❌   - qat-dashboard.ait.care (qa)');
  console.error('❌   - prod-dashboard.ait.care (production)');
  console.error('❌   - portal.ait.care (ind)');
  console.error('❌   - sandbox-dashboard.ait.care (sandbox)');
  console.error('❌ Set REACT_APP_ENV explicitly: REACT_APP_ENV=development|dev|qa|production|ind|sandbox');
    throw new Error(
    `Cannot detect environment from hostname: ${hostname}. ` +
    `Set REACT_APP_ENV=development|dev|qa|production|ind|sandbox explicitly.`
  );
};

const currentEnv = getEnvironment();

// Validate environment
const validEnvironments = ['development', 'dev', 'qa', 'production', 'ind', 'sandbox'];
if (!validEnvironments.includes(currentEnv)) {
  console.error(`❌ Invalid environment: ${currentEnv}`);
  console.error(`Valid environments: ${validEnvironments.join(', ')}`);
  throw new Error(
    `Invalid environment: ${currentEnv}. Must be one of: ${validEnvironments.join(', ')}`
  );
}

// Get static configuration for current environment
const staticConfig = ENV_CONFIG[currentEnv];

if (!staticConfig) {
  console.error(`❌ No configuration found for environment: ${currentEnv}`);
  throw new Error(`No configuration found for environment: ${currentEnv}`);
}

/**
 * Validate that an API URL is safe to use.
 *
 * Rules:
 *  1. Must be a valid URL (parseable by the URL constructor).
 *  2. Must use https: in production/qa/ind environments.
 *     http: is only permitted for localhost in development.
 *  3. Must not target known internal/metadata address ranges:
 *       - AWS instance metadata  (169.254.x.x)
 *       - Loopback outside dev   (127.x.x.x / ::1)
 *       - RFC-1918 private ranges (10.x, 172.16-31.x, 192.168.x)
 *
 * IMPORTANT - WHY THIS DOES NOT THROW:
 * This function previously threw Errors on validation failures. That caused a blank
 * page on deployment because environment.js runs at module load time (before React
 * renders). Any throw here crashes the entire JS bundle.
 * Instead we now log a console.error and return the fallback URL so the app
 * still renders and the developer can see a useful error in the browser console.
 */
const validateApiUrl = (url, env) => {
  let parsed;
  try {
    // Support relative URLs (deployed) by resolving against current origin
    parsed = new URL(url, window.location.origin);
  } catch {
    console.error(`Invalid API URL: "${url}" is not a valid URL. Falling back to relative /api/v1`);
    return '/api/v1';
  }

  // Rule 1: enforce HTTPS outside of local development
  const isLocalDev = env === 'development';
  if (!isLocalDev && parsed.protocol !== 'https:') {
    console.error(`Insecure API URL rejected: "${url}" must use https: in the ${env} environment.`);
    return `${window.location.origin}/api/v1`;
  }

  // Rule 2: block internal/metadata IP ranges
  const hostname = parsed.hostname;
  const BLOCKED_PATTERNS = [
    /^169\.254\./,           // AWS instance metadata service
    /^10\./,                 // RFC-1918 private (10.x.x.x)
    /^172\.(1[6-9]|2[0-9]|3[01])\./,  // RFC-1918 private (172.16-31.x.x)
    /^192\.168\./,           // RFC-1918 private (192.168.x.x)
    /^127\./,                // Loopback IPv4 (blocked outside dev)
    /^::1$/,                 // Loopback IPv6
    /^0\.0\.0\.0$/,          // Unspecified address
  ];

  // Allow 127.x and localhost only in development
  const isLocalhostUrl = hostname === 'localhost' || /^127\./.test(hostname);
  if (isLocalhostUrl && !isLocalDev) {
    console.error(`API URL "${url}" targets localhost which is not allowed in the ${env} environment. Falling back.`);
    return `${window.location.origin}/api/v1`;
  }

  // Block all other internal ranges unconditionally
  if (!isLocalhostUrl) {
    for (const pattern of BLOCKED_PATTERNS) {
      if (pattern.test(hostname)) {
        console.error(`API URL "${url}" targets a blocked internal address (${hostname}). Falling back.`);
        return `${window.location.origin}/api/v1`;
      }
    }
  }

  return url;
};

/**
 * Merge runtime configuration (from SSM) with static fallbacks.
 * Runtime config (window.APP_CONFIG) takes precedence.
 * This allows deployed environments to use SSM values while local dev uses static config.
 */
const mergeConfig = () => {
  const runtimeConfig = window.APP_CONFIG || {};

  const rawApiUrl = runtimeConfig.apiBaseUrl || staticConfig.API_URL;

  // Validate the API URL before it is exported — fail fast if something is wrong
  const safeApiUrl = validateApiUrl(rawApiUrl, currentEnv);

  return {
    API_URL: safeApiUrl,
    S3_BUCKET: runtimeConfig.dashboardPhotosBucket || staticConfig.S3_BUCKET,
    S3_REGION: staticConfig.S3_REGION,
    // Additional runtime values from SSM
    COGNITO_USER_POOL_ID: runtimeConfig.cognitoUserPoolId,
    COGNITO_CLIENT_ID: runtimeConfig.cognitoClientId,
    IDENTITY_POOL_ID: runtimeConfig.identityPoolId,
    RESIDENT_PHOTOS_BUCKET: runtimeConfig.residentPhotosBucket,
    FALL_CLIPS_BUCKET: runtimeConfig.fallClipsBucket,
  };
};

// Export merged configuration
export const config = mergeConfig();

// Export individual values for backward compatibility
export const API_BASE_URL = config.API_URL;
export const S3_BUCKET = config.S3_BUCKET;
export const S3_REGION = config.S3_REGION;

// Export current environment
export const ENVIRONMENT = currentEnv;

// Audit logging based on environment
if (currentEnv === 'development') {
  // Detailed logging for local development
  console.log('═══════════════════════════════════════════');
  console.log('🔧 ENVIRONMENT CONFIGURATION');
  console.log('═══════════════════════════════════════════');
  console.log('Environment:', currentEnv.toUpperCase());
  console.log('Runtime Mode:', process.env.NODE_ENV);
  console.log('Hostname:', window.location.hostname);
  console.log('───────────────────────────────────────────');
  console.log('🌐 API URL:', config.API_URL);
  console.log('☁️ S3 Bucket:', config.S3_BUCKET);
  console.log('📍 S3 Region:', config.S3_REGION);
  console.log('───────────────────────────────────────────');
  console.log('🔐 Cognito Pool:', config.COGNITO_USER_POOL_ID || '(using fallback)');
  console.log('🔐 Cognito Client:', config.COGNITO_CLIENT_ID || '(using fallback)');
  console.log('═══════════════════════════════════════════');
  
  // Warn if using static config instead of runtime config
  if (!window.APP_CONFIG) {
    console.warn('⚠️ No runtime configuration (window.APP_CONFIG) found');
    console.warn('⚠️ Using static fallback values - this is normal for local development');
    console.warn('⚠️ Deployed environments will use SSM-injected values');
  }
} else {
  // Minimal logging for deployed environments (security best practice)
  console.log(`✅ Environment: ${currentEnv.toUpperCase()}`);
  console.log(`✅ Configuration loaded successfully`);
  
  // Validate that runtime config is present in deployed environments
  if (!window.APP_CONFIG) {
    console.error('❌ CRITICAL: No runtime configuration found in deployed environment!');
    console.error('❌ window.APP_CONFIG is missing - SSM injection may have failed');
    console.error('❌ Check start.sh script and env-config.js generation');
  } else {
    console.log('✅ Runtime configuration loaded from SSM');
  }
}
